import { FileRenderTypes } from "@/enums/file-render-types.ts"
import { File as FileModel } from "@/models/file/file.model"
import { fileTypeFromBlob } from "file-type"

export class FileHelper {
  private static readonly wordExtensions = new Set(["doc", "docx"])
  private static readonly excelExtensions = new Set(["xls", "xlsx", "xlsm", "ods", "csv"])

  private static readonly supportedExtensions: { [extension: string]: FileRenderTypes } = {
    jpg: FileRenderTypes.IMAGE,
    jpeg: FileRenderTypes.IMAGE,
    png: FileRenderTypes.IMAGE,
    bmp: FileRenderTypes.IMAGE,
    gif: FileRenderTypes.IMAGE,
    avif: FileRenderTypes.IMAGE,
    webp: FileRenderTypes.IMAGE,

    pdf: FileRenderTypes.DOCUMENT,
    doc: FileRenderTypes.DOCUMENT,
    docx: FileRenderTypes.DOCUMENT,
    xls: FileRenderTypes.DOCUMENT,
    xlsx: FileRenderTypes.DOCUMENT,
    xlsm: FileRenderTypes.DOCUMENT,
    ods: FileRenderTypes.DOCUMENT,
    csv: FileRenderTypes.DOCUMENT,

    eml: FileRenderTypes.EMAIL,
    msg: FileRenderTypes.EMAIL,
    mbox: FileRenderTypes.EMAIL
  }

  /**
   * Returns the render type for the file based on its extension.
   * @param file The file object to check.
   * @returns The corresponding FileRenderTypes value or null if not found.
   */
  public static getFileRenderType(file?: FileModel): FileRenderTypes | null {
    if (!file) {
      return null
    }
    const fileExtension = file.extension.toLowerCase()
    return FileHelper.supportedExtensions[fileExtension] || null
  }

  /**
   * Checks if the file is supported based on its extension.
   * @param file
   */
  public static isSupportedFile(file: FileModel) {
    const fileExtension = file.extension.toLowerCase()
    return Object.keys(FileHelper.supportedExtensions).includes(fileExtension)
  }

  public static isWordDocument(file: FileModel) {
    return this.wordExtensions.has(file.extension.toLowerCase())
  }

  public static isSpreadsheet(file: FileModel) {
    return this.excelExtensions.has(file.extension.toLowerCase())
  }

  public static isPdfDocument(file: FileModel) {
    return file.extension.toLowerCase() === "pdf"
  }

  /**
   * Fetches the URL and returns its Blob.
   */
  private static async fetchBlob(url: string): Promise<Blob> {
    const res = await fetch(url)
    if (!res.ok) {
      throw new Error(`Failed to fetch ${url}: ${res.status}`)
    }
    return res.blob()
  }

  /**
   * Given a Blob and the original URL, detect its extension and mime,
   * then return a File with the correct filename (including extension).
   */
  private static async blobToFile(blob: Blob, originalUrl: string): Promise<File> {
    const type = await fileTypeFromBlob(blob)
    const ext = type?.ext ?? "bin"
    const mime = type?.mime

    // derive base name (strip any existing extension/query)
    const urlObj = new URL(originalUrl)
    const rawName = urlObj.pathname.split("/").pop() ?? "download"
    const baseName = rawName.replace(/\.[^/.]+$/, "")

    const filename = `${baseName}.${ext}`
    return new File([blob], filename, mime ? { type: mime } : undefined)
  }

  /**
   * Top-level API: download the URL and return a properly-named File.
   */
  public static async downloadFileFromURL(url: string): Promise<File> {
    const blob = await FileHelper.fetchBlob(url)
    return FileHelper.blobToFile(blob, url)
  }
}
